import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../../dto/data-model.js';
import { isSameData } from '../../dto/data-model-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { DailyGenericIncomeStats } from './daily-generic-income-stats.js';
import { Currency } from '../currency.js';
import {
  fromEnumAsString, toEnumFromString,
} from '../../../serialization/enum-utils.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { TimeZoneRegion } from '../time-zone-region.js';
import { DayOfYear } from '../day-of-year.js';
import { DailyTicketStats } from './daily-ticket-stats.js';

export class DailyIncomeStats extends DataModel {
  static readonly TYPE_ID: string = '051e412faf884251a407f03569e5b83e';

  constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly timezone: TimeZoneRegion,
    public readonly day: DayOfYear,
    public readonly currency: Currency,
    public readonly cashStats: DailyGenericIncomeStats,
    public readonly creditCardStats: DailyGenericIncomeStats,
    public readonly onlineStats: DailyGenericIncomeStats,
    public readonly ticketStats: DailyTicketStats,
  ) {
    super();
  }

  get $typeId(): string {
    return DailyIncomeStats.TYPE_ID;
  }

  static empty(
      businessRef: BusinessModuleRef,
      timezone: TimeZoneRegion,
      currency: Currency,
      day: DayOfYear,
  ): DailyIncomeStats {
    return new DailyIncomeStats(
        businessRef,
        timezone,
        day,
        currency,
        DailyGenericIncomeStats.empty(currency),
        DailyGenericIncomeStats.empty(currency),
        DailyGenericIncomeStats.empty(currency),
        DailyTicketStats.empty(),
    );
  }

  static fromMap(
      context: SerializationContext,
      map: SerializedData,
  ): DailyIncomeStats {
    return new DailyIncomeStats(
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion),
        context.fromData(MapUtils.get<SerializedData>(map, 'day')),
        toEnumFromString(MapUtils.get<string>(map, 'currency'), Currency),
        context.fromData(MapUtils.get<SerializedData>(map, 'cashStats')),
        context.fromData(MapUtils.get<SerializedData>(map, 'creditCardStats')),
        context.fromData(MapUtils.get<SerializedData>(map, 'onlineStats')),
        context.fromData(MapUtils.get<SerializedData>(map, 'ticketStats')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      businessRef: this.businessRef.encode(),
      timezone: fromEnumAsString(this.timezone),
      day: context.toData(this.day),
      currency: fromEnumAsString(this.currency),
      cashStats: context.toData(this.cashStats),
      creditCardStats: context.toData(this.creditCardStats),
      onlineStats: context.toData(this.onlineStats),
      ticketStats: context.toData(this.ticketStats),
    };
  }

  clone(): this {
    return new DailyIncomeStats(
        this.businessRef.clone(),
        this.timezone,
        this.day.clone(),
        this.currency,
        this.cashStats.clone(),
        this.creditCardStats.clone(),
        this.onlineStats.clone(),
        this.ticketStats.clone(),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
          other.timezone === this.timezone &&
          isSameData(other.day, this.day) &&
          other.currency === this.currency &&
          isSameData(other.cashStats, this.cashStats) &&
          isSameData(other.creditCardStats, this.creditCardStats) &&
          isSameData(other.onlineStats, this.onlineStats) &&
          isSameData(other.ticketStats, this.ticketStats);
  }
}

export class DailyIncomeStatsBuilder extends DataModelBuilder<DailyIncomeStats> {
  build(
      context: SerializationContext,
      map: SerializedData
  ): DailyIncomeStats {
    return DailyIncomeStats.fromMap(context, map);
  }
}
